import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API = 'https://tribaltech-backend.onrender.com';

function fmtDate(iso) {
  if (!iso) return null;
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}
// Date-only (YYYY-MM-DD) renderer. Parses as local day so a UTC-day
// string from the server doesn't display as the previous day in
// negative-offset timezones.
function fmtDateOnly(ymd) {
  if (!ymd) return null;
  const parts = String(ymd).split('-').map(Number);
  if (parts.length !== 3 || parts.some(n => !Number.isFinite(n))) return ymd;
  const [y, m, d] = parts;
  return new Date(y, m - 1, d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}
function daysSince(iso) {
  if (!iso) return null;
  return Math.floor((Date.now() - new Date(iso)) / 86400000);
}
function getDomain(url) {
  try { return new URL(url).hostname.replace('www.', ''); }
  catch { return url; }
}
function truncateUrl(url, max = 52) {
  if (!url) return '—';
  return url.length > max ? url.slice(0, max) + '…' : url;
}

// 2026-10-07: stage comes from the server — leak._display.stage is
// computed by lib/creator_display_label.computeStage(), the same single
// source of truth the Leaks page uses. The old branches that read
// leak.removed_at / leak.acknowledged_at / leak.dmca_filed_at did not
// work after 2026-10-05 08:50 EDT because those columns were removed
// from the /api/leaks whitelist (routes/leaks.js:creatorFacingShape),
// so every row surfaced as 'detected'. The pre-10-05 /api/leaks returned
// select('*') and the old branches worked by accident.
function getStatus(leak) {
  return (leak && leak._display && leak._display.stage) || 'detected';
}

const STATUS_CONFIG = {
  detected:        { label: 'Detected',        color: '#FB923C', bg: '#FB923C18', dot: '#FB923C' },
  dmca_filed:      { label: 'DMCA Filed',      color: '#A78BFA', bg: '#A78BFA18', dot: '#A78BFA' },
  acknowledged:    { label: 'Acknowledged',    color: '#60A5FA', bg: '#60A5FA18', dot: '#60A5FA' },
  removed:         { label: 'Removed',         color: '#34D399', bg: '#34D39918', dot: '#34D399' },
  no_longer_online:{ label: 'No longer online',color: '#94A3B8', bg: '#94A3B818', dot: '#94A3B8' },
  needs_review:    { label: 'Needs review',    color: '#F59E0B', bg: '#F59E0B18', dot: '#F59E0B' },
};

// 2026-10-08: timeline reads server-stamped _display dates (filed_on,
// acknowledged_on, removed_on) — the raw *_at columns are stripped by
// creatorFacingShape and never reach the client. A step is "done" only
// when its date exists; it renders skipped (dim, line-through, no
// "pending" text) if a later step is done but this one has no date;
// otherwise pending. no_longer_online rows swap the Removed step for a
// terminal "No longer online" with removed_on as its date; the DMCA
// Filed step is NOT done for them unless filed_on is set (which the
// server only sets with sent-notice evidence).
function buildSteps(leak) {
  const d = leak._display || {};
  const terminal = d.stage === 'no_longer_online';
  const detectedYmd = typeof leak.detected_at === 'string' ? leak.detected_at.slice(0, 10) : null;
  const base = [
    { key: 'detected',     label: 'Detected',     icon: '◎', date: detectedYmd,          cfg: STATUS_CONFIG.detected,     fmt: fmtDate, raw: leak.detected_at },
    { key: 'dmca_filed',   label: 'DMCA Filed',   icon: '⊟', date: d.filed_on || null,   cfg: STATUS_CONFIG.dmca_filed,   fmt: fmtDateOnly, raw: d.filed_on },
    { key: 'acknowledged', label: 'Acknowledged', icon: '◈', date: d.acknowledged_on || null, cfg: STATUS_CONFIG.acknowledged, fmt: fmtDateOnly, raw: d.acknowledged_on },
  ];
  const tail = terminal
    ? { key: 'no_longer_online', label: 'No longer online', icon: '○', date: d.removed_on || null, cfg: STATUS_CONFIG.no_longer_online, fmt: fmtDateOnly, raw: d.removed_on }
    : { key: 'removed',          label: 'Removed',          icon: '✓', date: d.removed_on || null, cfg: STATUS_CONFIG.removed,          fmt: fmtDateOnly, raw: d.removed_on };
  return [...base, tail];
}

function Timeline({ leak }) {
  const steps = buildSteps(leak);
  const laterDone = steps.map((_, i) => steps.slice(i + 1).some(s => !!s.date));
  return (
    <div style={tl.wrap}>
      {steps.map((step, i) => {
        const done    = !!step.date;
        const skipped = !done && laterDone[i];
        const next    = steps[i + 1];
        return (
          <div key={step.key} style={tl.step}>
            {i < steps.length - 1 && (
              <div style={{ ...tl.line, background: (next && next.date) ? step.cfg.color : 'rgba(255,255,255,0.08)' }} />
            )}
            <div style={{
              ...tl.node,
              background: done ? step.cfg.dot : 'transparent',
              border: `2px solid ${done ? step.cfg.dot : (skipped ? 'rgba(255,255,255,0.08)' : 'rgba(255,255,255,0.15)')}`,
              opacity: skipped ? 0.45 : 1,
            }}>
              {done && <span style={{ fontSize: 10, color: '#000', fontWeight: 700 }}>{step.icon}</span>}
            </div>
            <div style={tl.info}>
              <span style={{
                ...tl.stepLabel,
                color: done ? step.cfg.color : (skipped ? 'rgba(255,255,255,0.22)' : 'rgba(255,255,255,0.4)'),
                textDecoration: skipped ? 'line-through' : 'none',
              }}>{step.label}</span>
              {done ? (
                <span style={tl.stepTime}>{step.fmt(step.raw)}</span>
              ) : skipped ? (
                <span style={tl.stepSkipped}>skipped</span>
              ) : (
                <span style={tl.stepPending}>pending</span>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

const tl = {
  wrap: { display: 'flex', alignItems: 'flex-start', gap: 0, padding: '16px 0 8px', position: 'relative' },
  step: { display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1, position: 'relative' },
  line: { position: 'absolute', top: 11, left: '50%', width: '100%', height: 2, zIndex: 0 },
  node: { width: 24, height: 24, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1, flexShrink: 0, transition: 'all 0.3s ease' },
  info: { display: 'flex', flexDirection: 'column', alignItems: 'center', marginTop: 8, gap: 3 },
  stepLabel: { fontSize: 11, fontWeight: 600, letterSpacing: '0.04em', textAlign: 'center' },
  stepTime: { fontSize: 10, color: '#666', fontFamily: "'DM Mono', monospace", textAlign: 'center' },
  stepPending: { fontSize: 10, color: 'rgba(255,255,255,0.18)', fontStyle: 'italic', textAlign: 'center' },
  stepSkipped: { fontSize: 10, color: 'rgba(255,255,255,0.22)', fontStyle: 'italic', textAlign: 'center', textDecoration: 'line-through' },
};

function CaseCard({ leak, index }) {
  const [expanded, setExpanded] = useState(false);
  const status = getStatus(leak);
  const cfg = STATUS_CONFIG[status];
  const domain = getDomain(leak.leaked_url || '');
  const age = daysSince(leak.detected_at);
  async function markRemoved() {
    const token = localStorage.getItem('tribal_token');
    try {
      await axios.post(API + `/api/leaks/${leak.id}/mark-removed`, {}, {
        headers: { Authorization: 'Bearer ' + token },
      });
      window.location.reload();
    } catch (e) {
      alert('Mark removed failed: ' + (e.response?.data?.error || e.message));
    }
  }

  async function markAcknowledged() {
    const token = localStorage.getItem('tribal_token');
    try {
      await axios.post(API + `/api/leaks/${leak.id}/mark-acknowledged`, {}, {
        headers: { Authorization: 'Bearer ' + token },
      });
      window.location.reload();
    } catch (e) {
      alert('Mark acknowledged failed: ' + (e.response?.data?.error || e.message));
    }
  }

  return (
    <div style={{ ...card.wrap, animationDelay: `${index * 0.04}s` }} className="case-card">
      <div style={card.header} onClick={() => setExpanded(e => !e)}>
        <div style={card.left}>
          <span style={{ ...card.badge, background: cfg.bg, color: cfg.color, borderColor: cfg.color + '44' }}>{cfg.label}</span>
          <span style={card.domain}>{domain}</span>
          {leak.needs_review && <span style={card.reviewFlag}>⚠ review</span>}
        </div>
        <div style={card.right}>
          {age !== null && <span style={card.age}>{age}d ago</span>}
          {leak.confidence && <span style={card.confidence}>{Math.round(leak.confidence * 100)}% match</span>}
          <span style={{ ...card.chevron, transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)' }}>▾</span>
        </div>
      </div>
      <div style={card.urlRow}>
        <span style={card.url}>{truncateUrl(leak.leaked_url)}</span>
        {leak.leaked_url && <a href={leak.leaked_url} target="_blank" rel="noopener noreferrer" style={card.urlLink} onClick={e => e.stopPropagation()}>↗</a>}
      </div>
      <Timeline leak={leak} />
      {expanded && (() => {
        const d = leak._display || {};
        const noLongerOnline = d.stage === 'no_longer_online';
        const removedLabel   = noLongerOnline ? 'No longer online' : 'Removed';
        return (
          <div style={card.detail}>
            <div style={card.detailGrid}>
              {[
                { label: 'Detected',     value: leak.detected_at ? fmtDate(leak.detected_at) : '—' },
                { label: 'DMCA Filed',   value: d.filed_on ? fmtDateOnly(d.filed_on) : '—' },
                { label: 'Acknowledged', value: d.acknowledged_on ? fmtDateOnly(d.acknowledged_on) : '—' },
                { label: removedLabel,   value: d.removed_on ? fmtDateOnly(d.removed_on) : '—' },
                { label: 'Cohort ID',    value: leak.cohort_id || '—' },
              ].map(row => (
                <div key={row.label} style={card.detailRow}>
                  <span style={card.detailLabel}>{row.label}</span>
                  <span style={card.detailValue}>{row.value}</span>
                </div>
              ))}
            </div>
            <div style={card.fullUrl}>
              <span style={card.detailLabel}>Full URL</span>
              <a href={leak.leaked_url} target="_blank" rel="noopener noreferrer" style={card.fullUrlLink}>{leak.leaked_url}</a>
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
              {!d.acknowledged_on && !d.removed_on && (
                <button onClick={markAcknowledged} style={card.actionBtn}>
                  ◈ Mark acknowledged
                </button>
              )}
              {!d.removed_on && (
                <button onClick={markRemoved} style={{ ...card.actionBtn, background: '#34D39918', color: '#34D399', borderColor: '#34D39944' }}>
                  ✓ Mark as removed
                </button>
              )}
              {d.removed_on && (
                <span style={{ fontSize: 11, color: noLongerOnline ? '#94A3B8' : '#34D399', fontFamily: "'DM Mono', monospace" }}>
                  {noLongerOnline ? '○' : '✓'} {removedLabel} {fmtDateOnly(d.removed_on)}
                </span>
              )}
            </div>
          </div>
        );
      })()}
    </div>
  );
}

const card = {
  wrap: { background: '#0f0f1e', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 10, padding: '14px 16px', marginBottom: 8, animation: 'fadeUp 0.3s ease both', transition: 'border-color 0.2s' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', userSelect: 'none' },
  left: { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' },
  right: { display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 },
  badge: { fontSize: 10, padding: '3px 8px', borderRadius: 4, fontWeight: 700, letterSpacing: '0.06em', border: '1px solid', textTransform: 'uppercase' },
  domain: { fontSize: 12, color: '#888', fontFamily: "'DM Mono', monospace" },
  reviewFlag: { fontSize: 10, padding: '2px 7px', borderRadius: 4, background: '#fbbf2418', color: '#fbbf24', border: '1px solid #fbbf2444', fontWeight: 600 },
  age: { fontSize: 11, color: '#555', fontFamily: "'DM Mono', monospace" },
  confidence: { fontSize: 11, color: '#60A5FA', fontFamily: "'DM Mono', monospace" },
  chevron: { fontSize: 14, color: '#555', transition: 'transform 0.2s ease', lineHeight: 1 },
  urlRow: { display: 'flex', alignItems: 'center', gap: 8, marginTop: 6 },
  url: { fontSize: 11, color: '#555', fontFamily: "'DM Mono', monospace", flex: 1 },
  urlLink: { fontSize: 11, color: '#60A5FA', textDecoration: 'none', flexShrink: 0 },
  detail: { borderTop: '1px solid rgba(255,255,255,0.06)', marginTop: 12, paddingTop: 14 },
  detailGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 },
  detailRow: { display: 'flex', flexDirection: 'column', gap: 2 },
  detailLabel: { fontSize: 10, color: '#555', letterSpacing: '0.06em', textTransform: 'uppercase' },
  detailValue: { fontSize: 12, color: '#ccc', fontFamily: "'DM Mono', monospace" },
  notes: { marginTop: 12, padding: '10px 12px', background: 'rgba(255,255,255,0.03)', borderRadius: 6, borderLeft: '2px solid #A78BFA' },
  notesLabel: { fontSize: 10, color: '#A78BFA', letterSpacing: '0.06em', textTransform: 'uppercase' },
  notesText: { fontSize: 12, color: '#aaa', marginTop: 4, lineHeight: 1.6 },
  fullUrl: { marginTop: 10, display: 'flex', flexDirection: 'column', gap: 3 },
  fullUrlLink: { fontSize: 11, color: '#60A5FA', fontFamily: "'DM Mono', monospace", wordBreak: 'break-all', lineHeight: 1.5 },
  actionBtn: { fontSize: 11, padding: '5px 12px', borderRadius: 6, border: '1px solid #A78BFA44', background: '#A78BFA18', color: '#A78BFA', cursor: 'pointer', fontFamily: 'inherit', fontWeight: 600 },
};

export default function TakedownTracker() {
  const [leaks, setLeaks] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('newest');

  const load = useCallback(async () => {
    setLoading(true);
    const token = localStorage.getItem('tribal_token');
    try {
      const r = await axios.get(API + '/api/leaks', {
        headers: { Authorization: 'Bearer ' + token },
      });
      // 2026-10-07: /api/leaks has returned { leaks, stats } since
      // 2026-10-05 (commit 5bde17ab). Pre-2026-10-05 clients received a
      // bare array. Accept both so an older cached client still works.
      const payload = r.data || [];
      if (Array.isArray(payload)) {
        setLeaks(payload);
        setStats(null);
      } else {
        setLeaks(payload.leaks || []);
        setStats(payload.stats || null);
      }
    } catch (e) {
      console.error('Tracker load failed:', e.response?.data?.error || e.message);
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = leaks
    .filter(l => {
      if (filter !== 'all' && getStatus(l) !== filter) return false;
      if (search) return (l.leaked_url || '').toLowerCase().includes(search.toLowerCase());
      return true;
    })
    .sort((a, b) => {
      if (sortBy === 'newest') return new Date(b.detected_at) - new Date(a.detected_at);
      if (sortBy === 'oldest') return new Date(a.detected_at) - new Date(b.detected_at);
      if (sortBy === 'review') return (b.needs_review ? 1 : 0) - (a.needs_review ? 1 : 0);
      return 0;
    });

  // 2026-10-07: counts come from the server's stage-exclusive bucket
  // (stats.by_stage), not a browser-side reduce — so chip totals match
  // the per-row stage without recounting. If an older cached response
  // doesn't carry stats.by_stage, fall back to a client reduce over the
  // loaded rows (which is now exact since fetchAllRows removed the 1000
  // cap in commit 71d46914).
  const counts = (stats && stats.by_stage)
    ? stats.by_stage
    : leaks.reduce((acc, l) => { acc[getStatus(l)] = (acc[getStatus(l)] || 0) + 1; return acc; }, {});
  const totalCount = (stats && typeof stats.total_leaks === 'number') ? stats.total_leaks : leaks.length;

  const filterTabs = [
    { key: 'all',               label: 'All',               count: totalCount },
    { key: 'detected',          label: 'Detected',          count: counts.detected || 0 },
    { key: 'dmca_filed',        label: 'DMCA Filed',        count: counts.dmca_filed || 0 },
    { key: 'acknowledged',      label: 'Acknowledged',      count: counts.acknowledged || 0 },
    { key: 'removed',           label: 'Removed',           count: counts.removed || 0 },
    { key: 'no_longer_online',  label: 'No longer online',  count: counts.no_longer_online || 0 },
    { key: 'needs_review',      label: 'Needs review',      count: counts.needs_review || 0 },
  ];

  return (
    <div style={pg.shell}>
      <style>{`
        @keyframes fadeUp { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
        .case-card:hover { border-color: rgba(255,255,255,0.14) !important; }
        ::-webkit-scrollbar { width: 4px; } ::-webkit-scrollbar-track { background: transparent; } ::-webkit-scrollbar-thumb { background: #333; border-radius: 2px; }
      `}</style>
      <div style={pg.header}>
        <div>
          <h1 style={pg.title}>Takedown Tracker</h1>
          <p style={pg.sub}>{totalCount} cases · {counts.removed || 0} removed · {counts.dmca_filed || 0} in progress</p>
        </div>
        <button onClick={load} style={pg.refreshBtn} disabled={loading}>{loading ? '↻' : '↻ Refresh'}</button>
      </div>
      <div style={pg.summaryStrip}>
        {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
          <div key={key} style={pg.summaryItem}>
            <div style={{ ...pg.summaryDot, background: cfg.dot }} />
            <span style={pg.summaryLabel}>{cfg.label}</span>
            <span style={{ ...pg.summaryCount, color: cfg.color }}>{counts[key] || 0}</span>
          </div>
        ))}
      </div>
      <div style={pg.controls}>
        <div style={pg.tabs}>
          {filterTabs.map(t => (
            <button key={t.key} onClick={() => setFilter(t.key)} style={{ ...pg.tab, ...(filter === t.key ? pg.tabActive : {}) }}>
              {t.label}
              <span style={{ ...pg.tabCount, ...(filter === t.key ? pg.tabCountActive : {}) }}>{t.count}</span>
            </button>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <input placeholder="Search URL…" value={search} onChange={e => setSearch(e.target.value)} style={pg.search} />
          <select value={sortBy} onChange={e => setSortBy(e.target.value)} style={pg.select}>
            <option value="newest">Newest first</option>
            <option value="oldest">Oldest first</option>
            <option value="review">Needs review first</option>
          </select>
        </div>
      </div>
      <div style={pg.list}>
        {loading ? (
          <div style={pg.empty}>Loading cases…</div>
        ) : filtered.length === 0 ? (
          <div style={pg.empty}>{search ? 'No matches' : 'No cases yet'}</div>
        ) : (
          filtered.map((leak, i) => <CaseCard key={leak.id} leak={leak} index={i} />)
        )}
      </div>
    </div>
  );
}

const pg = {
  shell: { minHeight: '100vh', background: '#080810', color: '#e2e8f0', fontFamily: "'DM Sans', 'Helvetica Neue', sans-serif", padding: '0 0 60px' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '28px 28px 0' },
  title: { fontSize: 22, fontWeight: 700, color: '#fff', letterSpacing: '-0.02em' },
  sub: { fontSize: 12, color: '#555', marginTop: 4, fontFamily: "'DM Mono', monospace" },
  refreshBtn: { fontSize: 12, padding: '6px 14px', borderRadius: 6, border: '1px solid rgba(255,255,255,0.12)', background: 'rgba(255,255,255,0.05)', color: '#aaa', cursor: 'pointer', fontFamily: 'inherit', marginTop: 4 },
  summaryStrip: { display: 'flex', gap: 20, padding: '20px 28px 0', flexWrap: 'wrap' },
  summaryItem: { display: 'flex', alignItems: 'center', gap: 7 },
  summaryDot: { width: 8, height: 8, borderRadius: '50%', flexShrink: 0 },
  summaryLabel: { fontSize: 12, color: '#666' },
  summaryCount: { fontSize: 13, fontWeight: 700, fontFamily: "'DM Mono', monospace" },
  controls: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px 28px 0', flexWrap: 'wrap', gap: 12 },
  tabs: { display: 'flex', gap: 4 },
  tab: { fontSize: 12, padding: '6px 12px', borderRadius: 6, border: '1px solid transparent', background: 'transparent', color: '#666', cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: 6, transition: 'all 0.15s' },
  tabActive: { background: 'rgba(167,139,250,0.12)', color: '#A78BFA', borderColor: '#A78BFA44' },
  tabCount: { fontSize: 10, padding: '1px 5px', borderRadius: 3, background: 'rgba(255,255,255,0.07)', color: '#555' },
  tabCountActive: { background: '#A78BFA33', color: '#A78BFA' },
  search: { fontSize: 12, padding: '6px 12px', borderRadius: 6, border: '1px solid rgba(255,255,255,0.1)', background: '#0f0f1e', color: '#ccc', outline: 'none', fontFamily: 'inherit', width: 180 },
  select: { fontSize: 12, padding: '6px 10px', borderRadius: 6, border: '1px solid rgba(255,255,255,0.1)', background: '#0f0f1e', color: '#ccc', outline: 'none', fontFamily: 'inherit', cursor: 'pointer' },
  list: { padding: '16px 28px 0' },
  empty: { textAlign: 'center', color: '#444', fontSize: 13, padding: '60px 0', fontFamily: "'DM Mono', monospace" },
};
